import { useState } from 'react';

/* ---- Types ---- */
export interface BusinessHours {
  day: string;
  open: string;
  close: string;
  closed: boolean;
}

export interface EscalationRule {
  triggers: string;
  action: 'notify_email' | 'transfer_human' | 'end_conversation' | 'custom';
  message: string;
}

export interface Soul {
  tone: 'professional' | 'friendly' | 'casual' | 'authoritative';
  instructions: string;
  dos: string[];
  donts: string[];
  businessHours: BusinessHours[];
  escalation: EscalationRule;
}

export const DEFAULT_SOUL: Soul = {
  tone: 'professional',
  instructions: '',
  dos: [''],
  donts: [''],
  businessHours: [
    { day: 'Monday', open: '09:00', close: '17:00', closed: false },
    { day: 'Tuesday', open: '09:00', close: '17:00', closed: false },
    { day: 'Wednesday', open: '09:00', close: '17:00', closed: false },
    { day: 'Thursday', open: '09:00', close: '17:00', closed: false },
    { day: 'Friday', open: '09:00', close: '17:00', closed: false },
    { day: 'Saturday', open: '10:00', close: '14:00', closed: false },
    { day: 'Sunday', open: '', close: '', closed: true },
  ],
  escalation: {
    triggers: '',
    action: 'notify_email',
    message: '',
  },
};

const TONE_OPTIONS: {
  value: Soul['tone'];
  label: string;
  description: string;
  emoji: string;
}[] = [
  {
    value: 'professional',
    label: 'Professional',
    description: 'Polished and business-like. Uses proper language and maintains formality.',
    emoji: '\uD83D\uDCBC',
  },
  {
    value: 'friendly',
    label: 'Friendly',
    description: 'Warm and approachable. Conversational but still competent.',
    emoji: '\uD83D\uDE0A',
  },
  {
    value: 'casual',
    label: 'Casual',
    description: 'Relaxed and informal. Uses everyday language, feels like a friend.',
    emoji: '\uD83E\uDD19',
  },
  {
    value: 'authoritative',
    label: 'Authoritative',
    description: 'Expert and confident. Commands trust with decisive, knowledgeable responses.',
    emoji: '\uD83C\uDFAF',
  },
];

interface SoulEditorProps {
  soul: Soul;
  onChange: (soul: Soul) => void;
}

export default function SoulEditor({ soul, onChange }: SoulEditorProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const update = <K extends keyof Soul>(key: K, value: Soul[K]) => {
    onChange({ ...soul, [key]: value });
  };

  /* ---- Dynamic list helpers ---- */
  const updateListItem = (key: 'dos' | 'donts', index: number, value: string) => {
    const arr = [...soul[key]];
    arr[index] = value;
    update(key, arr);
  };

  const addListItem = (key: 'dos' | 'donts') => {
    update(key, [...soul[key], '']);
  };

  const removeListItem = (key: 'dos' | 'donts', index: number) => {
    if (soul[key].length <= 1) return;
    update(
      key,
      soul[key].filter((_, i) => i !== index)
    );
  };

  /* ---- Business hours helper ---- */
  const updateHours = (index: number, field: keyof BusinessHours, value: string | boolean) => {
    const hours = [...soul.businessHours];
    hours[index] = { ...hours[index], [field]: value };
    update('businessHours', hours);
  };

  return (
    <div className="space-y-6">
      {/* Tone Selector */}
      <div>
        <label className="block text-xs font-medium text-text-secondary uppercase tracking-wider mb-3">
          Personality Tone
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {TONE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => update('tone', option.value)}
              className={`text-left p-4 rounded-card border transition-all ${
                soul.tone === option.value
                  ? 'border-accent bg-accent/5 shadow-glow'
                  : 'border-border hover:border-border-hover bg-surface-tertiary'
              }`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-lg">{option.emoji}</span>
                <span
                  className={`text-sm font-semibold ${
                    soul.tone === option.value ? 'text-accent' : 'text-text-primary'
                  }`}
                >
                  {option.label}
                </span>
              </div>
              <p className="text-xs text-text-secondary leading-relaxed">{option.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Core Instructions */}
      <div>
        <label className="block text-xs font-medium text-text-secondary uppercase tracking-wider mb-2">
          Core Instructions
        </label>
        <textarea
          value={soul.instructions}
          onChange={(e) => update('instructions', e.target.value)}
          className="input-base w-full h-32 resize-y"
          placeholder="Tell your AI how to behave. For example: 'You are a helpful dental receptionist. Always recommend scheduling a consultation for complex questions. Never provide specific medical diagnoses...'"
        />
        <p className="text-xs text-text-muted mt-1">
          This is the primary instruction set for your AI. Be specific about what it should and should not do.
        </p>
      </div>

      {/* Do's */}
      <div>
        <label className="block text-xs font-medium text-text-secondary uppercase tracking-wider mb-2">
          Do's
        </label>
        <div className="space-y-2">
          {soul.dos.map((item, i) => (
            <div key={i} className="flex gap-2">
              <div className="flex items-center justify-center w-6 h-9 text-accent text-xs font-bold">
                +
              </div>
              <input
                type="text"
                value={item}
                onChange={(e) => updateListItem('dos', i, e.target.value)}
                className="input-base flex-1 h-9"
                placeholder="e.g., Always greet customers by name"
              />
              {soul.dos.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeListItem('dos', i)}
                  className="text-text-muted hover:text-red-400 px-2 transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => addListItem('dos')}
            className="text-xs text-accent hover:text-accent-dim font-medium flex items-center gap-1 mt-1"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Do
          </button>
        </div>
      </div>

      {/* Don'ts */}
      <div>
        <label className="block text-xs font-medium text-text-secondary uppercase tracking-wider mb-2">
          Don'ts
        </label>
        <div className="space-y-2">
          {soul.donts.map((item, i) => (
            <div key={i} className="flex gap-2">
              <div className="flex items-center justify-center w-6 h-9 text-red-400 text-xs font-bold">
                -
              </div>
              <input
                type="text"
                value={item}
                onChange={(e) => updateListItem('donts', i, e.target.value)}
                className="input-base flex-1 h-9"
                placeholder="e.g., Never discuss competitor pricing"
              />
              {soul.donts.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeListItem('donts', i)}
                  className="text-text-muted hover:text-red-400 px-2 transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => addListItem('donts')}
            className="text-xs text-red-400 hover:text-red-300 font-medium flex items-center gap-1 mt-1"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Don't
          </button>
        </div>
      </div>

      {/* Business Hours */}
      <div>
        <button
          type="button"
          onClick={() => setExpandedSection(expandedSection === 'hours' ? null : 'hours')}
          className="flex items-center justify-between w-full text-left"
        >
          <label className="block text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer">
            Business Hours
          </label>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={`text-text-muted transition-transform ${expandedSection === 'hours' ? 'rotate-180' : ''}`}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {expandedSection === 'hours' && (
          <div className="mt-3 space-y-2 animate-fade-in">
            {soul.businessHours.map((hours, i) => (
              <div
                key={hours.day}
                className="flex flex-wrap items-center gap-2"
              >
                <span className="text-xs text-text-secondary w-16 sm:w-20 shrink-0">{hours.day}</span>
                <label className="flex items-center gap-1.5 shrink-0 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hours.closed}
                    onChange={(e) => updateHours(i, 'closed', e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-border accent-accent"
                  />
                  <span className="text-xs text-text-muted">Closed</span>
                </label>
                {!hours.closed && (
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <input
                      type="time"
                      value={hours.open}
                      onChange={(e) => updateHours(i, 'open', e.target.value)}
                      className="input-base h-8 text-xs flex-1 min-w-[90px]"
                    />
                    <span className="text-text-muted text-xs">to</span>
                    <input
                      type="time"
                      value={hours.close}
                      onChange={(e) => updateHours(i, 'close', e.target.value)}
                      className="input-base h-8 text-xs flex-1 min-w-[90px]"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Escalation Rules */}
      <div>
        <button
          type="button"
          onClick={() => setExpandedSection(expandedSection === 'escalation' ? null : 'escalation')}
          className="flex items-center justify-between w-full text-left"
        >
          <label className="block text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer">
            Escalation Rules
          </label>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={`text-text-muted transition-transform ${expandedSection === 'escalation' ? 'rotate-180' : ''}`}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {expandedSection === 'escalation' && (
          <div className="mt-3 space-y-3 animate-fade-in">
            <div>
              <label className="block text-xs text-text-muted mb-1">Trigger phrases / conditions</label>
              <textarea
                value={soul.escalation.triggers}
                onChange={(e) =>
                  update('escalation', { ...soul.escalation, triggers: e.target.value })
                }
                className="input-base w-full h-20 resize-y"
                placeholder="e.g., 'speak to a human', complaints about billing, requests for refunds..."
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">Action</label>
              <select
                value={soul.escalation.action}
                onChange={(e) =>
                  update('escalation', {
                    ...soul.escalation,
                    action: e.target.value as EscalationRule['action'],
                  })
                }
                className="input-base w-full h-9"
              >
                <option value="notify_email">Notify via email</option>
                <option value="transfer_human">Transfer to human agent</option>
                <option value="end_conversation">End conversation gracefully</option>
                <option value="custom">Custom response</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">Escalation message</label>
              <input
                type="text"
                value={soul.escalation.message}
                onChange={(e) =>
                  update('escalation', { ...soul.escalation, message: e.target.value })
                }
                className="input-base w-full h-9"
                placeholder="e.g., Let me connect you with a team member who can help..."
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
